import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bot, ExternalLink, MessageSquare, Minimize2, PlayCircle, RefreshCw, Send, X } from 'lucide-react';
import { GlassButton, GlassInput } from '../ui';
import { api } from '../../hooks/useApi';

const pageLabels = {
  '/': 'Dashboard',
  '/financeiro': 'Financeiro',
  '/construtora': 'Construtora',
  '/rh': 'RH',
  '/frota': 'Frota',
  '/estoque': 'Estoque',
  '/imobiliaria': 'Imobiliária',
  '/marketing': 'Marketing',
  '/predictive': 'Predictive AI',
  '/settings': 'Administração',
  '/juridico': 'Jurídico',
  '/documentos': 'Documentos/OCR',
};

const quickPrompts = {
  '/': [
    'O que exige minha atenção hoje?',
    'Qual módulo devo alimentar primeiro?',
    'Explique os alertas do painel.',
  ],
  '/financeiro': [
    'Onde lanço uma conta a pagar?',
    'Quando uso caixa e quando uso competência?',
    'Como importar um PDF financeiro?',
  ],
  '/construtora': [
    'Como cadastro cliente, unidade e contrato?',
    'Como funciona correção por INCC/CUB/IGPM?',
    'O que falta para viabilidade de uma obra?',
  ],
  '/rh': [
    'Por que preciso documentos da empreiteira?',
    'Como registrar cartão ponto ou atestado?',
    'O que revisar antes de pagar uma medição?',
  ],
  '/frota': [
    'Como registro manutenção por foto ou PDF?',
    'Como vincular multa ao motorista certo?',
    'Onde controlo pneus e documentos?',
  ],
  '/estoque': [
    'Como controlo equipamento em obra?',
    'Quando baixa estoque da obra?',
    'O que significa manutenção vencida?',
  ],
  '/juridico': [
    'Como envio uma cobrança ao jurídico?',
    'Que documentos devo anexar ao processo?',
    'Como priorizo prazos e ações?',
  ],
  '/documentos': [
    'Para qual módulo devo encaminhar este documento?',
    'O que faço se o PDF for escaneado?',
    'Como conferir antes de gravar?',
  ],
  '/marketing': [
    'Como monto a Newsletter semanal?',
    'Como completo os dois imóveis pelo link?',
    'Quando uso Oportunidade especial?',
  ],
  '/predictive': [
    'Qual sinal devo resolver primeiro?',
    'Explique o score geral.',
    'O que depende de alimentação real?',
  ],
};

const normalizePath = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  return `/${pathname.split('/').filter(Boolean)[0] || ''}`;
};

export const ErpCopilot = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentModule = useMemo(() => normalizePath(location.pathname), [location.pathname]);
  const currentLabel = pageLabels[currentModule] || 'ERP';

  const ask = async () => {
    await askText(question);
  };

  const askText = async (text) => {
    if (!text.trim() || loading) return;
    const userText = text.trim();
    setQuestion('');
    setError('');
    setLoading(true);
    setMessages((current) => [...current, { role: 'user', text: userText }]);

    try {
      const response = await api.post('/predictive/copilot', {
        question: userText,
        page_context: location.pathname,
      });
      setMessages((current) => [...current, {
        role: 'assistant',
        text: response.data.answer,
        actions: response.data.assisted_actions || response.data.suggested_actions || [],
        preview: response.data.structured_preview || null,
      }]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Não consegui consultar o copiloto.');
    } finally {
      setLoading(false);
    }
  };

  const prompts = quickPrompts[currentModule] || [
    'O que posso fazer nesta página?',
    'Onde cadastro esta informação?',
    'Qual o próximo cuidado operacional?',
  ];

  const runAction = (action) => {
    if (action.prompt) {
      setQuestion(action.prompt);
    }
    if (action.path) {
      navigate(action.path);
      setIsOpen(false);
    }
  };

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+5rem)] right-4 z-[60] lg:bottom-5 lg:right-5 lg:z-50">
      {isOpen ? (
        <div className="flex h-[min(560px,calc(100dvh-9rem))] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-xl border border-white/15 bg-slate-950/95 shadow-2xl backdrop-blur-xl lg:h-[560px] lg:w-[380px] lg:max-w-[calc(100vw-2.5rem)] lg:rounded-2xl">
          <div className="flex items-center justify-between border-b border-white/10 p-3.5 lg:p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-200">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-white/90">Copiloto ERP</p>
                <p className="max-w-[190px] truncate text-xs text-white/45">Contexto: {currentLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button className="rounded-lg p-2 text-white/45 hover:bg-white/10 hover:text-white" onClick={() => setIsOpen(false)} aria-label="Minimizar copiloto" title="Minimizar">
                <Minimize2 className="h-4 w-4" />
              </button>
              <button className="rounded-lg p-2 text-white/45 hover:bg-white/10 hover:text-white" onClick={() => { setMessages([]); setError(''); }} aria-label="Limpar conversa" title="Limpar conversa">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-3.5 lg:p-4">
            {!messages.length && (
              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-start gap-3">
                  <MessageSquare className="mt-0.5 h-5 w-5 text-cyan-200" />
                  <div>
                    <p className="text-sm font-medium text-white/85">Pergunte sobre esta página</p>
	                    <p className="mt-1 text-sm leading-5 text-white/55">
	                      Posso explicar onde cadastrar algo, por que um documento é necessário ou qual próximo cuidado operacional olhar.
	                    </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {prompts.map((prompt) => (
                          <button
                            key={prompt}
                            type="button"
                            onClick={() => askText(prompt)}
                            className="rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-left text-xs text-white/65 hover:bg-white/10"
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
	                  </div>
	                </div>
	              </div>
            )}

            {messages.map((message, index) => (
              <div key={`${message.role}-${index}`} className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-5 ${
                  message.role === 'user'
                    ? 'bg-cyan-500/20 text-cyan-50'
                    : 'border border-white/10 bg-white/[0.04] text-white/75'
                }`}>
	                  <p>{message.text}</p>
                  {message.preview && (
                    <div className="mt-3 rounded-xl border border-cyan-300/15 bg-cyan-500/10 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-cyan-100/80">Prévia antes de abrir</p>
                      <p className="mt-1 text-sm font-medium text-white/85">{message.preview.titulo}</p>
                      <p className="mt-1 text-xs leading-5 text-white/55">{message.preview.resumo}</p>
                      {!!message.preview.campos_sugeridos?.length && (
                        <div className="mt-3 space-y-1">
                          {message.preview.campos_sugeridos.slice(0, 6).map((field) => (
                            <div key={`${field.campo}-${field.valor}`} className="flex justify-between gap-3 text-xs">
                              <span className="text-white/45">{field.label}</span>
                              <span className="max-w-[55%] truncate text-right text-white/75">{field.valor}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {!!message.preview.campos_pendentes?.length && (
                        <div className="mt-3 rounded-lg border border-amber-300/20 bg-amber-500/10 px-3 py-2">
                          <p className="text-xs text-amber-100/80">
                            Falta revisar: {message.preview.campos_pendentes.map((field) => field.label).join(', ')}
                          </p>
                        </div>
                      )}
                      <p className="mt-3 text-xs leading-5 text-white/45">{message.preview.seguranca}</p>
                    </div>
                  )}
	                  {!!message.actions?.length && (
                    <div className="mt-3 space-y-2">
                      {message.actions.slice(0, 4).map((action) => (
                        <button
                          key={`${action.label}-${action.path}`}
                          type="button"
                          onClick={() => runAction(action)}
                          className="flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-left text-xs text-white/65 hover:bg-white/10"
                        >
                          <span>
                            <span className="block font-medium text-white/75">{action.label}</span>
                            {action.description && <span className="mt-1 block text-white/40">{action.description}</span>}
                          </span>
                          {action.type === 'resolve_signal' ? (
                            <PlayCircle className="h-3.5 w-3.5 flex-shrink-0" />
                          ) : (
                            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex items-center gap-2 text-sm text-white/45">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Pensando com o contexto desta página...
              </div>
            )}

            {error && <p className="rounded-lg border border-red-400/20 bg-red-500/10 p-3 text-sm text-red-100">{error}</p>}
          </div>

          <div className="border-t border-white/10 p-3.5 lg:p-4">
            <div className="flex gap-2">
              <GlassInput
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') ask();
                }}
                placeholder="Pergunte ao copiloto..."
              />
	              <GlassButton size="icon" icon={loading ? RefreshCw : Send} onClick={ask} disabled={loading || !question.trim()} />
	            </div>
	          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className="flex h-14 w-14 items-center justify-center rounded-full border border-cyan-300/25 bg-cyan-500/20 text-cyan-100 shadow-2xl backdrop-blur-xl transition-transform hover:scale-105 hover:bg-cyan-500/30"
          title="Abrir Copiloto ERP"
          aria-label={`Abrir Copiloto ERP para ${currentLabel}`}
        >
          <Bot className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default ErpCopilot;
